import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import "./utils/errorBuffer"; // installe le buffer global des erreurs console (support)
import { fetchMyBranding, applyBranding } from "./utils/branding";
import SupportFloatingButton from "./components/support/SupportFloatingButton";
import IdleTimeoutManager from "./components/IdleTimeoutManager";
import SupportPage from "./pages/SupportPage";

// Un jeton exploitable : ni vide, ni la chaîne "null"/"undefined" (résidu de course
// entre login/logout). Envoyer « Bearer null » provoquait un 401 → logout global (iter127).
const readUsableToken = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    const v = String(t).trim();
    return (v === '' || v === 'null' || v === 'undefined') ? null : v;
  } catch (_) { return null; }
};

// Configure axios to automatically include auth token
axios.interceptors.request.use(
  (config) => {
    const token = readUsableToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Purge la session locale et redirige vers /login avec un message clair.
// Utilisé par l'intercepteur axios ET l'override fetch (couvre TOUS les appels API).
const handleGlobalAuthFailure = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch (_) { /* noop */ }
  // Évite de spammer si plusieurs 401 simultanés
  if (!window.__authRedirectInProgress) {
    window.__authRedirectInProgress = true;
    const current = window.location.pathname + window.location.search;
    const params = new URLSearchParams();
    params.set('reason', 'session');
    if (current && current !== '/login' && !current.startsWith('/login')) {
      params.set('next', current);
    }
    setTimeout(() => { window.location.href = `/login?${params.toString()}`; }, 50);
  }
};

// URLs pour lesquelles un 401 ne doit PAS déconnecter l'application :
// login (mauvais identifiants), logout, changement de mot de passe (erreur métier),
// endpoints publics, téléchargements (message local).
const isAuthExemptUrl = (url) =>
  url.includes('/auth/login') || url.includes('/auth/logout') ||
  url.includes('/auth/stay-connected') || url.includes('/auth/heartbeat') ||
  url.includes('/auth/change-password') || url.includes('/auth/password') ||
  url.includes('/public/') || url.includes('/f/') || url.includes('/download');

// Tentative unique de "reclaim" silencieux avant tout logout global : appelle
// /auth/stay-connected (auth léniente) qui ré-adopte la session si l'user est bien
// le propriétaire du token. Anti-cascade : max 1 tentative en cours à la fois.
let _reclaimInFlight = null;
const attemptSilentReclaim = async () => {
  if (_reclaimInFlight) return _reclaimInFlight;
  const token = readUsableToken();
  if (!token) return { ok: false };
  const backend = process.env.REACT_APP_BACKEND_URL;
  _reclaimInFlight = (async () => {
    try {
      const r = await originalFetch(`${backend}/api/auth/stay-connected`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      return { ok: r.ok, status: r.status };
    } catch (_) {
      return { ok: false, status: 0 };
    } finally {
      // Libère le flag après 2 s pour permettre un nouveau reclaim si nécessaire
      setTimeout(() => { _reclaimInFlight = null; }, 2000);
    }
  })();
  return _reclaimInFlight;
};

// Réponse : détecte les 401 (token expiré/invalide/session remplacée) → tente d'abord
// un reclaim silencieux, ne déclenche le logout global que si le reclaim échoue.
axios.interceptors.response.use(
  (response) => {
    // lot X3 — une réponse qui aboutit efface le bandeau de reconnexion.
    if (window.__komercesReseauKO) signalerReseau(false);
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';

    // lot X3 — panne réseau (serveur qui redémarre, coupure chez l'utilisateur) : bandeau +
    // réessais croissants, logique partagée avec les autres instances axios. Le 401 est
    // traité plus bas, inchangé.
    if (estPanneReseau(error) && error?.config) {
      const rejoue = await rejouerApresPanne(error, axios);
      if (rejoue) return rejoue;
      return Promise.reject(error);
    }

    if (status === 401 && !isAuthExemptUrl(url) && !error?.config?.__reclaimTried) {
      const claim = await attemptSilentReclaim();
      if (claim.ok) {
        // Rejouer la requête d'origine une seule fois
        try {
          error.config.__reclaimTried = true;
          return await axios.request(error.config);
        } catch (retryErr) {
          if (retryErr?.response?.status === 401) handleGlobalAuthFailure();
          return Promise.reject(retryErr);
        }
      }
      handleGlobalAuthFailure();
    } else if (status === 401 && !isAuthExemptUrl(url)) {
      handleGlobalAuthFailure();
    }
    return Promise.reject(error);
  }
);

// Override global fetch to include auth token automatically
const originalFetch = window.fetch;
// Expose le fetch BRUT (sans header Authorization) pour les appels tiers (ex. HIBP).
window.__rawFetch = originalFetch.bind(window);
window.fetch = async (url, options = {}) => {
  const token = readUsableToken();
  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }
  const response = await originalFetch(url, options);
  // Session expirée / remplacée : tente d'abord un reclaim silencieux avant logout global.
  try {
    const u = typeof url === 'string' ? url : (url && url.url) || '';
    if (response && response.status === 401 && token && !isAuthExemptUrl(u) && !options.__reclaimTried) {
      const claim = await attemptSilentReclaim();
      if (claim.ok) {
        // Rejouer la requête d'origine une seule fois (headers actualisés avec le token courant)
        const retryOpts = { ...options, __reclaimTried: true };
        const fresh = readUsableToken();
        retryOpts.headers = fresh
          ? { ...retryOpts.headers, Authorization: `Bearer ${fresh}` }
          : { ...retryOpts.headers };
        return await originalFetch(url, retryOpts);
      }
      handleGlobalAuthFailure();
    }
  } catch (_) { /* noop */ }
  return response;
};

import { useTheme } from "./contexts/ThemeContext";
import SidebarLayout from "./components/layouts/SidebarLayout";
import RouterSync from "./components/RouterSync";
import ImpersonationBanner from "./components/ImpersonationBanner";
import NetworkBanner from "./components/NetworkBanner";
import { signalerReseau, estPanneReseau, rejouerApresPanne } from "./utils/reseauResilience";
import { TYPES_BIEN_BY_TRANSACTION, ACTIVITY_TYPES_BY_BIEN_TYPE_MAPPING } from './constants/propertyMappings';
import PropertiesListPage from './pages/PropertiesListPage';
import PropertyViewPage from './pages/PropertyViewPage';
const PropertyEditPage = React.lazy(() => import('./pages/PropertyEditPage'));
import PropertyPublicPage from './pages/PropertyPublicPage';
import PropertyWizardPage from './pages/PropertyWizardPage';
import DiffusionPage from './pages/DiffusionPage';

import { 
  $getRoot,
  $getSelection,
  $createParagraphNode,
  $createTextNode
} from 'lexical';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeNode } from '@lexical/code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// AJ3 — chargement en deux temps du carnet de contacts (modèle du lot AG). La première page
// s'affiche tout de suite, le carnet complet suit en arrière-plan car la recherche et les
// filtres tournent dans le navigateur. Borne haute explicite = plafond de `GET /contacts`.
const CONTACTS_PREMIERE_PAGE = 20;
const CONTACTS_BORNE_HAUTE = 10000;

// Imported Components
import ShareModal from './components/shared/ShareModal';
import SharedCollectionView from './components/shared/SharedCollectionView';
import AvisDeValeur from './components/AvisDeValeur';
import SharedPropertyDetail from './components/shared/SharedPropertyDetail';
import PropertyCard from './components/properties/PropertyCard';
import PropertyActions from './components/properties/PropertyActions';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Sidebar from './components/Sidebar';
import ContactRequests from './pages/ContactRequests';
import ModernTopBar from './components/ModernTopBar';
import BottomNavigation from './components/layouts/BottomNavigation';
import CadreMenu from './components/layouts/CadreMenu';
import { useLocation } from 'react-router-dom';

// Wrapper qui masque la BottomNav sur les routes publiques (fiche partagée, login)
const ConditionalBottomNav = () => {
  const location = useLocation();
  const path = location.pathname || '';
  // Masquer sur : /f/{token} (fiche publique), /p/{code} (legacy public), /shared/, /login
  if (path.startsWith('/f/') || path.startsWith('/p/') || path.startsWith('/shared/') || path === '/login') {
    return null;
  }
  // BottomNavigation est autonome : elle gère elle-même toutes ses navigations
  // via useNavigate() (voir /app/frontend/src/components/layouts/BottomNavigation.js).
  return <BottomNavigation />;
};
import ContactList from './pages/ContactList';
import ContactForm from './pages/ContactForm';
const ContactViewMode = React.lazy(() => import('./pages/ContactViewMode'));
import GroupesPage from './pages/GroupesPage';
import ContactFichePrint from './pages/ContactFichePrint';
import NouvelleVisitePage from './pages/NouvelleVisitePage';
import ContactExpressPage from './pages/ContactExpressPage';
import VisiteDetailPage from './pages/VisiteDetailPage';
import TasksPage from './pages/TasksPage';
import MySubscriptionPage from './pages/MySubscriptionPage';
const SuperAdminConfigPage = React.lazy(() => import('./pages/SuperAdminConfigPage'));
const SuperAdminSubscriptionPage = React.lazy(() => import('./pages/SuperAdminSubscriptionPage'));
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForcePasswordResetPage from './pages/ForcePasswordResetPage';
import LogoutPage from './pages/LogoutPage';
const SuperAdminPage = React.lazy(() => import('./pages/SuperAdminPage'));
import AdminAgencePage from './pages/AdminAgencePage';
import MarketingPage from './pages/MarketingPage';
import ObservatoirePage from './pages/ObservatoirePage';
import NotificationsPage from './pages/NotificationsPage';
import { NotificationsProvider } from './contexts/NotificationsContext';


// ============================================
// COMPOSANTS EXTRAITS - Étape 1 Refactoring
// ============================================
import Header from './components/Header';
import PropertyWizard from './components/property/PropertyWizard';
import PropertyForm from './components/property/PropertyForm';
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { syncContactTagsByType } from "./utils/contactProfile";

// Import des nouveaux composants
import DocumentsView from './DocumentsView';
import ModuleGate from './components/ModuleGate';
import ErrorBoundary from './components/ErrorBoundary';
import PublicPropertyView from './PublicPropertyView';

// Import navigation utils
import { ROUTES, adresseRetourListe } from './utils/navigation';

// Composant de redirection dynamique pour les routes avec paramètres
const RedirectWithParams = ({ to }) => {
  const params = useParams();
  let path = to;
  // Remplacer les paramètres dans le chemin
  Object.keys(params).forEach(key => {
    path = path.replace(`:${key}`, params[key]);
  });
  return <Navigate to={path} replace />;
};

// ============================================
// ContactListWrapper - Charge les contacts depuis l'API
// ============================================
function ContactListWrapper({
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [contactSearchFilters, setContactSearchFilters] = useState({});
  // AJ3 — le carnet complet est-il chargé ? (la recherche navigateur en a besoin)
  const [contactsChargementComplet, setContactsChargementComplet] = useState(true);
  const contactsChargementEnCoursRef = useRef(false);

  // Charger les contacts au montage et quand showArchivedContacts change
  useEffect(() => {
    loadContacts();
  }, [showArchivedContacts]);

  const loadContacts = async (explicitStatus) => {
    setLoading(true);
    try {
      const status = explicitStatus || (showArchivedContacts ? 'archived' : 'active');
      // AJ3 — chargement en DEUX TEMPS, sur le modèle du lot AG (PropertiesListPage) :
      // 2,1 Mo et 5 s s'écoulaient avant le premier affichage alors que l'écran n'en montre
      // que 20. On NE BORNE PAS la liste pour autant : sa recherche et ses filtres tournent
      // dans le navigateur et ont besoin de tout le carnet. Borne haute explicite à 10 000,
      // qui est le plafond de `GET /contacts`.
      const premiere = await axios.get(`${API}/contacts?status=${status}&limit=${CONTACTS_PREMIERE_PAGE}`);
      const debut = premiere.data || [];
      setContacts(debut);
      const carnetIncomplet = debut.length >= CONTACTS_PREMIERE_PAGE;
      setContactsChargementComplet(!carnetIncomplet);
      setLoading(false);

      // Second temps. ⚠️ Piège du lot AA : ce chargement n'est JAMAIS déclenché par un rendu,
      // il est lancé ici, une seule fois par appel. La garde interdit deux passes concurrentes.
      if (carnetIncomplet && !contactsChargementEnCoursRef.current) {
        contactsChargementEnCoursRef.current = true;
        axios.get(`${API}/contacts?status=${status}&limit=${CONTACTS_BORNE_HAUTE}`)
          .then(res => {
            setContacts(res.data || debut);
            setContactsChargementComplet(true);
          })
          .catch(err => {
            console.error('Erreur second chargement contacts:', err);
            setContactsChargementComplet(true);
          })
          .finally(() => { contactsChargementEnCoursRef.current = false; });
      }
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
      setContacts([]);
      setLoading(false);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      loadContacts();
    } catch (error) {
      if (error.response?.status === 409) {
        const d = error.response.data?.detail || {};
        const count = (d.linked_properties || []).length;
        alert(`Suppression impossible : ${count} bien(s) actif(s) sont liés à ce propriétaire. Gérez-les d'abord (archivez ou réaffectez).`);
      } else {
        console.error('Erreur suppression contact:', error);
        alert('Erreur lors de la suppression du contact');
      }
    }
  };

  const bulkArchiveContacts = async (contactIds) => {
    const ids = contactIds || selectedContacts;
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map(id => 
        axios.put(`${API}/contacts/${id}`, { status: 'archived' })
      ));
      setSelectedContacts([]);
      loadContacts();
    } catch (error) {
      console.error('Erreur archivage contacts:', error);
    }
  };

  const bulkUnarchiveContacts = async (contactIds) => {
    const ids = contactIds || selectedContacts;
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map(id => 
        axios.put(`${API}/contacts/${id}`, { status: 'active' })
      ));
      setSelectedContacts([]);
      loadContacts();
    } catch (error) {
      console.error('Erreur désarchivage contacts:', error);
    }
  };

  const bulkDeleteContacts = async () => {
    if (selectedContacts.length === 0) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedContacts.length} contact(s) ?`)) return;
    try {
      const { data } = await axios.post(`${API}/contacts/bulk-delete`, selectedContacts);
      if (data.blocked && data.blocked.length > 0) {
        const lines = data.blocked
          .map(b => `• ${b.nom} — ${b.linked_properties.length} bien(s) actif(s) lié(s)`)
          .join('\n');
        alert(
          `${data.deleted_count} contact(s) supprimé(s).\n\n` +
          `${data.blocked.length} propriétaire(s) NON supprimé(s) car des biens actifs leur sont liés :\n${lines}\n\n` +
          `Gérez d'abord leurs biens (archivez ou réaffectez).`
        );
      }
      setSelectedContacts([]);
      loadContacts();
    } catch (error) {
      console.error('Erreur suppression contacts:', error);
      alert('Erreur lors de la suppression groupée');
    }
  };

  const handleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar 
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar 
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          tasksCount={0}
          notificationsCount={0}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          <ContactList 
            contacts={contacts}
            loading={loading}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            startNewContact={navHandlers.startNewContact}
            editContact={(contact) => navigate(`/contacts/${contact.id}`)}
            viewContact={(contact) => navigate(`/contacts/${contact.id}`)}
            deleteContact={deleteContact}
            showArchivedContacts={showArchivedContacts}
            setShowArchivedContacts={setShowArchivedContacts}
            loadContacts={loadContacts}
            contactSearchFilters={contactSearchFilters}
            setContactSearchFilters={setContactSearchFilters}
            contactTypes={['Acquéreur', 'Propriétaire', 'Prospect', 'Notaire', 'Syndic']}
            contactGroupes={['VIP', 'Clients', 'Acquéreurs', 'Propriétaires', 'Syndics']}
            bulkArchiveContacts={bulkArchiveContacts}
            bulkUnarchiveContacts={bulkUnarchiveContacts}
            bulkDeleteContacts={bulkDeleteContacts}
            handleContactSelection={handleContactSelection}
            chargementComplet={contactsChargementComplet}
            handleSelectAllContacts={handleSelectAllContacts}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// ContactDetailWrapper - Pour voir/éditer/créer un contact
// ============================================
function ContactDetailWrapper({
  mode, // 'view', 'edit', 'create'
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(mode !== 'create');
  const [isEditing, setIsEditing] = useState(mode === 'create');

  // lot DA2 — création express appelée depuis un autre écran (ex. « Nouvelle visite ») :
  // préremplissage de la saisie et du rôle, puis retour sur l'écran d'origine.
  const retourApresCreation = searchParams.get('retour');
  const prefill = React.useMemo(() => {
    if (mode !== 'create') return null;
    const nom = searchParams.get('nom');
    const type = searchParams.get('type');
    if (!nom && !type) return null;
    return syncContactTagsByType({
      nom: nom || '',
      type_contact: type || '',
      groupes: [],
      negociateur_id: sidebarUserProfile?.id || sidebarUserProfile?.userId || '',
    });
  }, [mode, searchParams, sidebarUserProfile]);

  // Synchroniser isEditing avec le mode (édition globale dépréciée — voir effet redirect)
  useEffect(() => {
    setIsEditing(mode === 'create');
  }, [mode]);

  // L'édition globale /contacts/:id/edit est dépréciée : toute édition passe par les
  // crayons des blocs (pattern unique). On redirige vers la fiche en consultation.
  useEffect(() => {
    if (mode === 'edit' && id) {
      navigate(`/contacts/${id}`, { replace: true });
    }
  }, [mode, id, navigate]);

  useEffect(() => {
    if (id && mode !== 'create') {
      loadContact();
    } else if (mode === 'create') {
      // Réinitialiser pour éviter le pré-remplissage avec le contact précédemment consulté
      setContact(null);
      setLoading(false);
    }
  }, [id, mode]);

  const loadContact = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contacts/${id}`);
      setContact(response.data);
    } catch (error) {
      console.error('Erreur chargement contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (contactData) => {
    try {
      if (mode === 'create') {
        const response = await axios.post(`${API}/contacts`, contactData);
        if (retourApresCreation) {
          const separateur = retourApresCreation.includes('?') ? '&' : '?';
          navigate(`${retourApresCreation}${separateur}contact=${response.data.id}`);
          return;
        }
        alert('✅ Contact créé avec succès !');
        // Après création, aller sur la fiche en consultation (édition par blocs)
        navigate(`/contacts/${response.data.id}`);
      } else {
        await axios.put(`${API}/contacts/${id}`, contactData);
        alert('✅ Contact enregistré avec succès !');
        // Rester sur la fiche en mode édition, juste recharger les données
        loadContact();
      }
    } catch (error) {
      console.error('Erreur sauvegarde contact:', error);
      alert('❌ Erreur lors de la sauvegarde du contact. Veuillez réessayer.');
    }
  };

  const handleCancel = () => {
    // Toujours retourner à la liste des contacts
    navigate('/contacts');
  };

  const handleEdit = () => {
    navigate(`/contacts/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      navigate('/contacts');
    } catch (error) {
      console.error('Erreur suppression contact:', error);
    }
  };

  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar 
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar 
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          tasksCount={0}
          notificationsCount={0}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : isEditing || mode === 'create' ? (
            <ContactForm 
              key={mode === 'create' ? 'new-contact' : id}
              contact={mode === 'create' ? prefill : contact}
              onSave={handleSave}
              onCancel={handleCancel}
              onView={() => navigate(`/contacts/${id}`)}
              isNew={mode === 'create'}
            />
          ) : (
            <ContactViewMode 
              contact={contact}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onClose={() => navigate('/contacts')}
              loadContact={loadContact}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// GroupesWrapper - Vue groupes (tags) avec layout
// ============================================
function GroupesWrapper({
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar 
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar 
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          tasksCount={0}
          notificationsCount={0}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          <GroupesPage />
        </div>
      </div>
    </div>
  );
}

// ============================================
// VisiteWrapper — pages Visite dans le cadre de l'application (lot DA n°2)
// ============================================
function VisiteWrapper({
  children,
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          tasksCount={0}
          notificationsCount={0}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================
// TasksWrapper - Page Tâches avec layout
// ============================================
function TasksWrapper({
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar 
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar 
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          <TasksPage />
        </div>
      </div>
    </div>
  );
}

// ============================================
// DocumentsWrapper - Hub Documents avec layout
// ============================================
function DocumentsWrapper({
  sidebarOpen, setSidebarOpen,
  sidebarUserProfile, agencySettings,
  navHandlers, sidebarDemandesCount, favoritePropertiesList
}) {
  return (
    <div className="flex h-screen relative">
      <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
        <Sidebar 
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          {...navHandlers}
          demandesContactCount={sidebarDemandesCount}
          onClose={() => setSidebarOpen(false)}
        />
      </CadreMenu>
      <div className="flex-1 flex flex-col overflow-hidden">
        <ModernTopBar 
          startNewProperty={navHandlers.startNewProperty}
          startNewContact={navHandlers.startNewContact}
          globalSearch={''}
          setGlobalSearch={() => {}}
          handleSearchFilterChange={() => {}}
          executeSearch={() => {}}
          showFavoritesView={navHandlers.showFavoritesView}
          favoriteProperties={favoritePropertiesList}
          tasksCount={0}
          notificationsCount={0}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="flex-1 overflow-auto pb-24 md:pb-20">
          <ErrorBoundary>
            <DocumentsView />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

// ============================================
// AppContent - Composant interne avec useNavigate()
// ============================================
function AppContent({ 
  appShowWizard, setAppShowWizard, 
  propertyToEdit, setPropertyToEdit,
  sidebarUserProfile, setSidebarUserProfile, agencySettings,
  sidebarDemandesCount, favoritePropertiesList,
  sidebarOpen, setSidebarOpen,
  propertyFormRef, isModerne, isJoueurs
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Garde BLOQUANTE « réinitialisation obligatoire » ──────────────────
  // Si l'utilisateur a le flag force_password_reset_at_next_login, on n'affiche
  // QUE l'écran /force-reset-password — aucun rendu du dashboard ni des routes
  // protégées (pas de flash de contenu, pas de fuite de données via F12).
  const forceResetPending = (() => {
    if (!localStorage.getItem('token')) return false;
    // 🔐 G8 (audit Fable 5) : en impersonation Super Admin, on N'AFFICHE JAMAIS le flow
    // « Sécurisez votre compte » du compte cible (consultation seule — sinon le Super Admin
    // définirait le mot de passe du client à sa place et casserait son onboarding réel).
    if (localStorage.getItem('impersonating') === 'true') return false;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.force_password_reset_at_next_login === true;
    } catch {
      return false;
    }
  })();
  const _path = location.pathname;
  const _exemptFromReset = _path === '/login' || _path === '/logout'
    || _path.startsWith('/f/') || _path.startsWith('/p/') || _path.startsWith('/shared/');

  // Navigation handlers (SPA sans reload)
  const navHandlers = {
    showPropertiesView: () => { navigate(ROUTES.PROPERTIES); setSidebarOpen(false); },
    showContactsView: () => { navigate(ROUTES.CONTACTS); setSidebarOpen(false); },
    showDemandesContactView: () => { navigate(ROUTES.DEMANDES_CONTACT); setSidebarOpen(false); },
    showFavoritesView: () => { navigate(`${ROUTES.PROPERTIES}?filter=favorites`); setSidebarOpen(false); },
    showMarketingView: () => { navigate(ROUTES.MARKETING); setSidebarOpen(false); },
    showOutilsView: () => { navigate(ROUTES.OUTILS); setSidebarOpen(false); },
    setActiveMarketingTab: (tab) => { navigate(`${ROUTES.MARKETING}?tab=${tab}`); setSidebarOpen(false); },
    setActiveOutilsTab: (tab) => { navigate(`${ROUTES.OUTILS}?tab=${tab}`); setSidebarOpen(false); },
    // MODIFIÉ : Navigation canonique vers /properties/new au lieu de /?action=new
    startNewProperty: () => { navigate('/properties/wizard/new'); setSidebarOpen(false); },
    startNewContact: () => { navigate('/contacts/new'); setSidebarOpen(false); },
    showDashboardView: () => { navigate(ROUTES.DASHBOARD); setSidebarOpen(false); },
    showAvisDeValeurView: () => { navigate(`${ROUTES.OUTILS}/avis-de-valeur`); setSidebarOpen(false); },
    consultProperty: (property) => { navigate(ROUTES.PROPERTY_VIEW(property.id)); },
    selectProperty: (property) => { navigate(`/properties/${property.id}/edit`); },
    // lot CE — retour depuis la fiche : l'adresse mémorisée de la liste (filtres, page, tri).
    // ⛔ `navigate(-1)` retiré : le miroir du lot V1 écrit l'adresse en `replace`, donc l'entrée
    // d'historique précédente est la liste AVANT pagination — on retombait en page 1. Et depuis
    // un lien direct ou le tableau de bord, il ramenait au mauvais écran.
    goBack: () => { navigate(adresseRetourListe()); },
    editProperty: (property) => { navigate(`/properties/${property.id}/edit`); },
  };

  // Garde bloquante : tout chemin non exempté est remplacé par l'écran force-reset.
  if (forceResetPending && !_exemptFromReset) {
    return (
      <Routes>
        <Route path="*" element={<ForcePasswordResetPage />} />
      </Routes>
    );
  }

  // ── Garde BLOQUANTE « authentification » ──────────────────────────────
  // Sans token, on ne rend AUCUNE route protégée (zéro fetch, zéro flash de
  // données tenant pour un visiteur non authentifié). Seules les routes publiques
  // (login, fiche partagée /f/, /shared/, /p/) restent accessibles. RLS fail-closed
  // côté front, en miroir du backend (require_auth → 401).
  const _hasToken = !!localStorage.getItem('token');
  const _isPublicPath = _path === '/login' || _path === '/logout'
    || _path === '/forgot-password' || _path === '/reset-password'
    || _path.startsWith('/f/') || _path.startsWith('/p/') || _path.startsWith('/shared/');
  if (!_hasToken && !_isPublicPath) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/f/:token" element={<PropertyPublicPage />} />
        <Route path="/shared/:token" element={<SharedCollectionView />} />
        <Route path="/shared/:token/:propertyId" element={<SharedPropertyDetail />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Routes d'authentification - sans sidebar */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/force-reset-password" element={<ForcePasswordResetPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/super-admin" element={<SuperAdminPage />} />
      <Route path="/super-admin/config" element={<SuperAdminConfigPage />} />
      <Route path="/super-admin/agences/:agenceId/subscription" element={<SuperAdminSubscriptionPage />} />
      <Route path="/admin-agence" element={<AdminAgencePage />} />
      
      {/* Route PUBLIQUE partageable — accès sans authentification (Sprint 1) */}
      <Route path="/f/:token" element={<PropertyPublicPage />} />
      
      {/* Route Marketing avec sidebar et header */}
      <Route path="/marketing" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <ModuleGate slug="marketing">
                <MarketingPage />
              </ModuleGate>
            </div>
          </div>
        </div>
      } />

      {/* Route Observatoire — lot DI (statistiques BODACC, module stats-simulateurs) */}
      {/* Route Notifications — lot DJ (socle, aucun module) */}
      <Route path="/notifications" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <NotificationsPage />
            </div>
          </div>
        </div>
      } />

      <Route path="/observatoire" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <ModuleGate slug="stats-simulateurs">
                <ObservatoirePage />
              </ModuleGate>
            </div>
          </div>
        </div>
      } />


      {/* Route Diffusion — LOT 3 iter92 (tableau biens × supports) */}
      <Route path="/diffusion" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <DiffusionPage />
            </div>
          </div>
        </div>
      } />
      
      {/* Route création nouveau bien - DOIT ÊTRE AVANT /properties */}
      <Route path="/properties/new" element={
        <PropertyEditPage
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          demandesContactCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
          navHandlers={navHandlers}
          setSidebarUserProfile={setSidebarUserProfile}
          isCreating={true}
        />
      } />
      
      {/* Route canonique /properties */}
      <Route path="/properties" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <PropertiesListPage 
                consultProperty={navHandlers.consultProperty}
                selectProperty={navHandlers.selectProperty}
                startNewProperty={navHandlers.startNewProperty}
                favoriteProperties={favoritePropertiesList}
                onToggleFavorite={(propertyId) => {
                  // Ne pas re-gérer le toggle ici - PropertiesListPage s'en occupe
                  // Juste déclencher un événement pour forcer la synchro
                  window.dispatchEvent(new Event('storage'));
                  console.log('✅ onToggleFavorite appelé pour:', propertyId);
                }}
                userProfile={sidebarUserProfile}
                agencySettings={agencySettings}
                onPropertiesChange={(properties) => {
                  console.log('Properties updated:', properties.length);
                }}
              />
            </div>
          </div>
        </div>
      } />
      
      {/* Redirection /biens-v2 → /properties */}
      <Route path="/biens-v2" element={<Navigate to="/properties" replace />} />
      
      {/* Route liste contacts */}
      <Route path="/contacts" element={
        <ContactListWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />
      
      {/* Route aperçu imprimable fiche contact (plein écran) */}
      <Route path="/contacts/:id/fiche" element={<ContactFichePrint />} />
      {/* lot DD — Contact express (saisie rapide, téléphone) */}
      <Route path="/contacts/express" element={
        <VisiteWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        >
          <ContactExpressPage />
        </VisiteWrapper>
      } />
      {/* lot DA — Visites (bon de visite, dénonce) */}
      <Route path="/visites/nouvelle" element={
        <VisiteWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        >
          <NouvelleVisitePage />
        </VisiteWrapper>
      } />
      <Route path="/visites/:id" element={
        <VisiteWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        >
          <VisiteDetailPage />
        </VisiteWrapper>
      } />

      {/* Route consultation contact */}
      <Route path="/contacts/:id" element={
        <ContactDetailWrapper 
          mode="view"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />
      
      {/* Route édition contact */}
      <Route path="/contacts/:id/edit" element={
        <ContactDetailWrapper 
          mode="edit"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />
      
      {/* Route nouveau contact */}
      <Route path="/contacts/new" element={
        <ContactDetailWrapper 
          mode="create"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />

      {/* Route Groupes (vue 2 colonnes : groupes + contacts) */}
      <Route path="/groupes" element={
        <GroupesWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />

      {/* Route Tâches */}
      <Route path="/taches" element={
        <TasksWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />

      {/* Route Mon Abonnement (Admin Agence, lecture seule Phase 1) */}
      <Route path="/mon-abonnement" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <MySubscriptionPage />
            </div>
          </div>
        </div>
      } />
      
      {/* Redirection /biens-v2 → /properties */}
      <Route path="/biens-v2" element={<Navigate to="/properties" replace />} />
      
      {/* Route consultation bien */}
      <Route path="/properties/:id/view" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <PropertyViewPage 
                userProfile={sidebarUserProfile}
                agencySettings={agencySettings}
                onBack={navHandlers.goBack}
                onEdit={navHandlers.editProperty}
              />
            </div>
          </div>
        </div>
      } />
      
      {/* Redirection ancienne route consultation */}
      <Route path="/biens-v2/:id/view" element={<RedirectWithParams to="/properties/:id/view" />} />
      
      {/* Route édition bien */}
      <Route path="/properties/:id/edit" element={
        <PropertyEditPage
          userProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          demandesContactCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
          navHandlers={navHandlers}
        />
      } />
      
      {/* Routes additionnelles */}
      <Route path="/properties/wizard/new" element={<PropertyWizardPage />} />
      <Route path="/demandes-contact" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <ContactRequests />
            </div>
          </div>
        </div>
      } />
      <Route path="/documents" element={
        <DocumentsWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />
      <Route path="/documents/:tab" element={
        <DocumentsWrapper
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarUserProfile={sidebarUserProfile}
          agencySettings={agencySettings}
          navHandlers={navHandlers}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
        />
      } />
      <Route path="/p/:code" element={<PublicPropertyView />} />
      <Route path="/shared/:token" element={<SharedCollectionView />} />
      <Route path="/shared/:token/:propertyId" element={<SharedPropertyDetail />} />
      {/* La page « Mes signalements » vit dans la coquille de l'application (barre latérale +
          barre du haut), comme toutes les pages internes. Elle était déclarée nue. */}
      <Route path="/support" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <SupportPage />
            </div>
          </div>
        </div>
      } />
      <Route path="/parametres" element={<Navigate to="/settings" replace />} />
      <Route path="/outils/avis-de-valeur" element={
        <div className="flex h-screen relative">
          <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
            <Sidebar 
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
              {...navHandlers}
              demandesContactCount={sidebarDemandesCount}
              onClose={() => setSidebarOpen(false)}
            />
          </CadreMenu>
          <div className="flex-1 flex flex-col overflow-hidden">
            <ModernTopBar 
              startNewProperty={navHandlers.startNewProperty}
              startNewContact={navHandlers.startNewContact}
              globalSearch={''}
              setGlobalSearch={() => {}}
              handleSearchFilterChange={() => {}}
              executeSearch={() => {}}
              showFavoritesView={navHandlers.showFavoritesView}
              favoriteProperties={favoritePropertiesList}
              tasksCount={0}
              notificationsCount={0}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="flex-1 overflow-auto pb-24 md:pb-20">
              <ModuleGate slug="avis-de-valeur">
                <AvisDeValeur />
              </ModuleGate>
            </div>
          </div>
        </div>
      } />
      
      {/* Route catch-all pour PropertyForm */}
      <Route path="*" element={
        appShowWizard ? 
          <PropertyWizard 
            onClose={() => setAppShowWizard(false)} 
            onPropertyCreated={(propertyId) => {
              setAppShowWizard(false);
              setPropertyToEdit(propertyId);
            }}
          /> :
          isModerne ? (
            <div className="flex h-screen relative">
              <CadreMenu ouvert={sidebarOpen} onFermer={() => setSidebarOpen(false)}>
                <Sidebar 
                  userProfile={sidebarUserProfile}
                  agencySettings={agencySettings}
                  {...navHandlers}
                  demandesContactCount={sidebarDemandesCount}
                  onClose={() => setSidebarOpen(false)}
                />
              </CadreMenu>
              <div className="flex-1 flex flex-col overflow-hidden">
                <ModernTopBar 
                  startNewProperty={navHandlers.startNewProperty}
                  startNewContact={navHandlers.startNewContact}
                  globalSearch={''}
                  setGlobalSearch={() => {}}
                  handleSearchFilterChange={() => {}}
                  executeSearch={() => {}}
                  showFavoritesView={navHandlers.showFavoritesView}
                  favoriteProperties={favoritePropertiesList}
                  tasksCount={0}
                  notificationsCount={0}
                  onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                />
                <div className="flex-1 overflow-auto pb-24 md:pb-20">
                  <PropertyForm
                    ref={propertyFormRef}
                    propertyToEdit={propertyToEdit}
                    onPropertyEditComplete={() => setPropertyToEdit(null)}
                    onStartWizard={() => setAppShowWizard(true)}
                    userProfile={sidebarUserProfile}
                    agencySettings={agencySettings}
                  />
                </div>
              </div>
            </div>
          ) : (
            <PropertyForm
              ref={propertyFormRef}
              propertyToEdit={propertyToEdit}
              onPropertyEditComplete={() => setPropertyToEdit(null)}
              onStartWizard={() => setAppShowWizard(true)}
              userProfile={sidebarUserProfile}
              agencySettings={agencySettings}
            />
          )
      } />
    </Routes>
  );
}

// ============================================
// App - Composant principal avec BrowserRouter
// ============================================
function App() {
  const [appShowWizard, setAppShowWizard] = useState(false);
  const [propertyToEdit, setPropertyToEdit] = useState(null);
  const [sidebarUserProfile, setSidebarUserProfile] = useState({
    civilite: "",
    prenom: "",
    nom: "",
    email: "",
    telephone: ""
  });
  const [agencySettings, setAgencySettings] = useState({
    nom_agence: "",
    email: "",
    telephone: "",
    adresse: "",
    ville: "",
    logo_url: ""
  });
  const [sidebarDemandesCount, setSidebarDemandesCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [favoritePropertiesList, setFavoritePropertiesList] = useState([]);
  
  // Sidebar state for mobile/tablet responsive
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Ref pour accéder aux fonctions de PropertyForm
  const propertyFormRef = React.useRef();
  
  // Hook de thème
  const { isModerne, isJoueurs, switchTheme } = useTheme();
  
  // State to track authentication changes
  const [authToken, setAuthToken] = useState(localStorage.getItem('token'));
  
  // Listen for storage changes (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('token');
      if (newToken !== authToken) {
        setAuthToken(newToken);
      }
    };
    
    // Listen to storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for same-tab changes
    const interval = setInterval(handleStorageChange, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [authToken]);
  
  // Load user profile and agency settings directly in App wrapper
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/user`, { headers });
        const userData = response.data;
        setSidebarUserProfile({
          civilite: userData.civilite || "M.",
          prenom: userData.prenom || "",
          nom: userData.nom || "",
          email: userData.email || "",
          telephone: userData.telephone || ""
        });
        
        // Apply theme
        if (userData.theme && switchTheme) {
          switchTheme(userData.theme);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };
    
    const loadAgencySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/agency`, { headers });
        const agencyData = response.data;
        setAgencySettings({
          nom_agence: agencyData.nom_agence || "",
          email: agencyData.email || agencyData.email_agence || "",
          telephone: agencyData.telephone || agencyData.telephone_agence || "",
          adresse: agencyData.adresse || agencyData.adresse_complete || "",
          ville: agencyData.ville || "",
          logo_url: agencyData.logo_url || ""
        });
      } catch (error) {
        console.error('Error loading agency settings:', error);
      }
    };
    
    const _tok = localStorage.getItem('token');
    if (!_tok) { return; }  // pas de token → ne rien fetcher (zéro 401 inutile, zéro fuite)
    loadProfile();
    loadAgencySettings();
  }, [switchTheme, authToken]);

  // Branding agence (app authentifiée) : variables CSS de couleur + favicon + logo sidebar
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      applyBranding(null);
      return;
    }
    fetchMyBranding().then((b) => applyBranding(b)).catch(() => {});
  }, [authToken]);

  // Sync demandesContact count and favoriteProperties from PropertyForm
  // lot CA — 🔴 cette minuterie tournait toutes les 100 ms en appelant `setFavoritePropertiesList`
  // avec un NOUVEAU tableau à chaque tour : toute l'application se re-rendait 10 fois par seconde,
  // et les URL de logos (qui portaient un horodatage) étaient redemandées en continu.
  // Désormais : 1 seconde, et aucun `set…` si la valeur n'a pas changé.
  const derniersFavorisRef = useRef(null);
  useEffect(() => {
    const interval = setInterval(() => {
      if (propertyFormRef.current?.demandesContact) {
        // Compter seulement les demandes non traitées (nouveau + en attente)
        const unprocessedCount = propertyFormRef.current.demandesContact.filter(
          d => d.statut === 'nouveau' || d.statut === 'en_attente' || d.statut === 'en_cours'
        ).length;
        setSidebarDemandesCount(prev => (prev === unprocessedCount ? prev : unprocessedCount));
      }

      // Toujours charger les favoris depuis localStorage
      try {
        const saved = localStorage.getItem('favoriteProperties');
        if (saved === derniersFavorisRef.current) return;   // ⛔ rien n'a changé : aucun rendu
        derniersFavorisRef.current = saved;
        const favs = saved ? JSON.parse(saved) : [];
        setFavoritePropertiesList(favs);
        setFavoritesCount(favs.length);
      } catch (e) {
        console.error('Erreur lecture favoris:', e);
        derniersFavorisRef.current = null;
        setFavoritePropertiesList([]);
        setFavoritesCount(prev => (prev === 0 ? prev : 0));
      }
    }, 1000); // lot CA — 1 s : un favori ajouté apparaît en moins de 2 s

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App">
      <NetworkBanner />
      <ImpersonationBanner />
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <RouterSync propertyFormRef={propertyFormRef} />
        {/* lot DJ — compteur de notifications partagé par la barre, la cloche et la page */}
        {/* Suspense : les pages lourdes (édition de bien, super admin…) sont chargées
            à la demande (React.lazy) pour accélérer le premier affichage. */}
        <React.Suspense fallback={
          <div className="flex items-center justify-center min-h-screen" data-testid="route-loading">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        }>
        <NotificationsProvider>
        <AppContent
          appShowWizard={appShowWizard}
          setAppShowWizard={setAppShowWizard}
          propertyToEdit={propertyToEdit}
          setPropertyToEdit={setPropertyToEdit}
          sidebarUserProfile={sidebarUserProfile}
          setSidebarUserProfile={setSidebarUserProfile}
          agencySettings={agencySettings}
          sidebarDemandesCount={sidebarDemandesCount}
          favoritePropertiesList={favoritePropertiesList}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          propertyFormRef={propertyFormRef}
          isModerne={isModerne}
          isJoueurs={isJoueurs}
        />
        </NotificationsProvider>
        </React.Suspense>
        <ConditionalBottomNav />
        <SupportFloatingButton />
        {authToken && <IdleTimeoutManager key={authToken} />}
      </BrowserRouter>
    </div>
  );
}

export default App;
